import { Checkbox, Grid, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';
import Loading from 'components/Loading';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { RolesEnum } from 'modules/authenticate/interfaces';
import i18n from 'i18n';
import PermissionCard from 'containers/Groups/PermissionCard';
import Alert from '@material-ui/lab/Alert';
import styles from '../../Template/styles';
import { ENTITY_NAME } from './Content';

const useStyles = makeStyles(styles);

interface Props {
  title: string;
  subtitle: string;
  isLoading: boolean;
  permissions?: {
    loading: boolean;
    selected: string[];
    available: { name: string; selected: boolean }[];
  };
  pending: string[];
  role?: RolesEnum;
  setPending: (pending: string[]) => void;
  updatePermissions: (permissions: string[]) => void;
}

const ListPermissions = ({
  title,
  subtitle,
  permissions,
  pending,
  role,
  isLoading,
  setPending,
  updatePermissions,
}: Props) => {
  const classes = useStyles();
  const [checked, setChecked] = React.useState<string[]>([]);

  const permissionsNames: string[] = permissions?.available?.map(
    item => item.name
  ) || [''];

  const permissionList = [
    {
      key: 'instance',
      label: i18n.t('INSTANCES_KEY'),
      values: [
        'list_environment_instances',
        'can_block_environment_instances',
        'can_terminate_environment_instances',
        'can_restart_environment_instances',
        'can_register_environment_instances',
        'can_connect_instance',
        'list_environment_instance_sessions',
        'can_activate_environment_instances',
        'can_unblock_environment_instances',
        'can_unregister_environment_instances',
        'delete_environment_instance_sessions',
        'validate_instance_zero_trust',
      ].filter(item => {
        const hidePermissionsForOperator =
          [
            'can_block_environment_instances',
            'can_unblock_environment_instances',
          ].includes(item) &&
          (role === RolesEnum.operator || role === RolesEnum.clientOperator);

        if (hidePermissionsForOperator) return false;
        return permissionsNames.includes(item);
      }),
    },
    {
      key: 'maintenance',
      label: i18n.t('MAINTENANCE_KEY'),
      values: [
        'get_environment_maintenance',
        'edit_environment_maintenance',
        'environment_maintenance_history',
        'can_edit_env_scheduled_warnings',
        'can_list_env_scheduled_warnings_details',
        'edit_env_auto_remove_user_session_maintenance',
        'list_env_session_removal_instances',
        'edit_env_session_removal_instances',
        'list_env_auto_block_instances_details',
        'edit_env_auto_block_instances_maintenance',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'preparation-server',
      label: i18n.t('GROUPS_PAGE.PREPARATION_SERVERS_KEY'),
      values: [
        'list_preparation_servers',
        'attach_preparation_server',
        'detach_preparation_server',
        'start_preparation_server',
        'stop_preparation_server',
        'can_block_actions_preparation_server',
        'can_unblock_actions_preparation_server',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'support-server',
      label: i18n.t('GROUPS_PAGE.SUPPORT_SERVERS_KEY'),
      values: [
        'list_support_servers',
        'attach_support_server',
        'detach_support_server',
        'start_support_server',
        'stop_support_server',
        'can_enable_server_monitoring_management',
        'can_disable_server_monitoring_management',
        'can_view_server_monitoring_status',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'environment',
      label: i18n.t('ENVIRONMENTS_KEY'),
      values: [
        'list_environment',
        'list_provider_resources',
        'list_environment_dashboard',
        'list_environment_details',
        'delete_environment',
        'list_environment_cost',
        'can_view_environment_licenses',
        'list_dashboard_performance',
        'can_manage_environment_status',
        'can_accept_pending_extension',
        'can_export_environment_summary_list',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'scaling-advisor',
      label: 'Scaling Advisor',
      values: [
        'list_scaling_advisor_details',
        'list_scaling_advisors',
        'edit_scaling_advisor',
        'can_ignore_scaling_advisor',
        'can_apply_scaling_advisor',
        'can_revert_scaling_advisor',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'client',
      label: i18n.t('CLIENTS_KEY'),
      values: [
        'can_import_clients',
        'create_new_client',
        'edit_password_reqs',
        'list_license_groups',
        'create_license_group',
        'edit_license_group',
        'delete_license_group',
        'edit_license_group_clients',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'model',
      label: i18n.t('MODELS_KEY'),
      values: [
        'list_models',
        'create_models',
        'edit_models',
        'delete_models',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'config',
      label: i18n.t('CONFIG_KEY'),
      values: [
        'edit_behavior',
        'edit_environment',
        'edit_governance',
        'list_applications_environment',
        'create_applications_environment',
        'change_applications_name_environment',
        'remove_applications_environment',
        'change_applications_state_environment',
        'change_applications_attachment_environment',
        'can_edit_environment_monitoring_settings',
        'can_edit_clients_in_bulk',
        'reset_administrative_pin',
        'get_administrative_pin',
      ].filter(item => permissionsNames.includes(item)),
    },
    {
      key: 'image',
      label: i18n.t('IMAGES_KEY'),
      values: [
        'publish_image',
        'publish_image_v2',
        'list_image_publication_schedule',
        'create_image_publication_schedule',
        'cancel_image_publication_schedule',
      ].filter(item => permissionsNames.includes(item)),
    },
  ].filter(item => item.values.length > 0);

  const handleCheck = {
    exists: (value: string) => checked.includes(value),
    toggle: (value: string) => {
      setPending(
        pending.includes(value)
          ? pending.filter(item => item !== value)
          : pending.concat(value)
      );

      const newCheckeds = checked.includes(value)
        ? checked.filter(item => item !== value)
        : [...checked, value];

      setChecked(newCheckeds);
      updatePermissions(newCheckeds);
    },
  };

  React.useEffect(() => {
    if (permissions?.selected) {
      setChecked(permissions.selected);
    }
  }, [permissions]);

  const allPermissionsAvailable = React.useMemo(() => permissionsNames || [], [
    permissionsNames,
  ]);

  const handleCheckAllExists = (values: string[]) =>
    values.reduce(
      (permissionsResponse, currentValue) =>
        permissionsResponse && checked.includes(currentValue),
      true
    );

  const handleToggleAll = (values: string[]) => {
    let newListPending = [...pending];
    values.forEach(value => {
      if (newListPending.includes(value)) {
        newListPending = newListPending.filter(item => item !== value);
      } else {
        newListPending = [...newListPending, value];
      }
    });
    setPending(newListPending);
    if (handleCheckAllExists(values)) {
      setChecked([]);
      updatePermissions([]);
    } else {
      setChecked(values);
      updatePermissions(values);
    }
  };

  return (
    <React.Fragment>
      {permissionList.length === 0 ? (
        <Grid container justify="center" className={classes.warning}>
          <Alert className={classes.alert} severity="warning" color="info">
            <Grid item>
              <Typography>{i18n.t('NO_PERMISSIONS_IN_GROUP_KEY')}</Typography>
            </Grid>
          </Alert>
        </Grid>
      ) : (
        <Grid
          container
          spacing={2}
          className={clsx({
            [classes.disableArea]: permissions?.loading,
          })}
        >
          <Grid item xs={12} style={{ margin: '32px 0 8px' }}>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2">{subtitle}</Typography>
          </Grid>
          {(permissions?.loading && !permissionsNames) || isLoading ? (
            <Loading />
          ) : (
            <>
              <Grid container className={classes.superTitle}>
                <Checkbox
                  size="small"
                  checked={handleCheckAllExists(allPermissionsAvailable)}
                  onChange={() => {
                    handleToggleAll(allPermissionsAvailable);
                  }}
                  className={classes.checkIcon}
                />
                <Typography className={classes.titleText}>
                  {i18n.t('GROUPS_PAGE.SELECT_ALL_KEY')}
                </Typography>
              </Grid>
              <PermissionCard
                groupPermissions={permissionList}
                entityKey="environment"
                permissions={permissions}
                pending={pending}
                handleCheck={handleCheck}
                handleCheckAllExists={handleCheckAllExists}
                setPending={setPending}
                updatePermissions={updatePermissions}
                checked={checked}
                setChecked={setChecked}
              />
            </>
          )}
        </Grid>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  permission: { permissions, isLoading },
}: ApplicationState) => ({
  permissions: permissions[ENTITY_NAME],
  isLoading,
  role: data?.role,
});

export default connect(mapStateToProps)(ListPermissions);
